import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatChipsModule } from '@angular/material/chips'
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule } from '@angular/material/dialog'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatSelectModule } from '@angular/material/select'
import { MatTooltip } from '@angular/material/tooltip'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { firstValueFrom } from 'rxjs'
import { ConfirmDialogComponent } from '../../../confirm-dialog/confirm-dialog.component'
import { TimestampPipe } from '../../../timestamp.pipe'
import {
  MeetingEntitiesOverview,
  MeetingEntitiesService,
  MeetingEntity,
  MeetingEntityInterval,
  MeetingEntityTemplateItem,
  parseTimestamp,
} from '../meeting-entities.service'
import { MeetingEntityReadiness, inheritsEntityScope, resolveMeetingEntityReadiness } from '../meeting-entity-readiness'

export type ManageMeetingEntitiesDialogData = {
  meetingId: number
}

type IntervalDraft = {
  meetingEntityId: number
  /** The agenda item the interval is the entity's discussion of; null for the entity as a whole. */
  templateItemId: number | null
  start: string
  end: string
  isInvalid: boolean
}

type OrderedTemplateItem = {
  item: MeetingEntityTemplateItem
  depth: number
}

@Component({
  selector: 'app-manage-meeting-entities-dialog',
  standalone: true,
  imports: [
    AsyncPipe,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTooltip,
    NgTemplateOutlet,
    TimestampPipe,
    TranslateDirective,
    TranslatePipe,
  ],
  templateUrl: './manage-meeting-entities-dialog.component.html',
  styleUrl: './manage-meeting-entities-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ManageMeetingEntitiesDialogComponent {
  data: ManageMeetingEntitiesDialogData = inject(MAT_DIALOG_DATA)
  private meetingEntitiesService = inject(MeetingEntitiesService)
  private dialog = inject(MatDialog)
  private translateService = inject(TranslateService)

  overview$ = this.meetingEntitiesService.getOverview(this.data.meetingId)

  private createEntityMutation = this.meetingEntitiesService.createEntityMutation()
  private importFromAgendaMutation = this.meetingEntitiesService.importFromAgendaMutation()
  private renameEntityMutation = this.meetingEntitiesService.renameEntityMutation()
  private reorderEntitiesMutation = this.meetingEntitiesService.reorderEntitiesMutation()
  private deleteEntityMutation = this.meetingEntitiesService.deleteEntityMutation()
  private replaceIntervalsMutation = this.meetingEntitiesService.replaceIntervalsMutation()
  private updateTemplateItemScopeMutation = this.meetingEntitiesService.updateTemplateItemScopeMutation()
  private generateEntityMinutesMutation = this.meetingEntitiesService.generateEntityMinutesMutation()
  private generateAllEntityMinutesMutation = this.meetingEntitiesService.generateAllEntityMinutesMutation()

  newEntityName = ''
  intervalDraft: IntervalDraft | null = null

  readiness(overview: MeetingEntitiesOverview, entity: MeetingEntity): MeetingEntityReadiness {
    return resolveMeetingEntityReadiness(overview, entity)
  }

  hasEntitySpecificItems(overview: MeetingEntitiesOverview): boolean {
    return overview.templateItems.some((item) => item.isEntitySpecific)
  }

  canGenerateAny(overview: MeetingEntitiesOverview): boolean {
    return overview.entities.some((entity) => resolveMeetingEntityReadiness(overview, entity) === 'READY')
  }

  /** The template in reading order, each item with how deep it sits, for a list indented like the agenda. */
  orderTemplateItems(items: MeetingEntityTemplateItem[]): OrderedTemplateItem[] {
    const childrenOf = (parentId: number | null): MeetingEntityTemplateItem[] =>
      items.filter((item) => item.parentId === parentId).toSorted((first, second) => first.index - second.index)
    const walk = (parentId: number | null, depth: number): OrderedTemplateItem[] =>
      childrenOf(parentId).flatMap((item) => [{ item, depth }, ...walk(item.templateItemId, depth + 1)])
    return walk(null, 0)
  }

  inheritsEntityScope(item: MeetingEntityTemplateItem, items: MeetingEntityTemplateItem[]): boolean {
    return inheritsEntityScope(item, items)
  }

  async addEntity() {
    const name = this.newEntityName.trim()
    if (name === '') {
      return
    }
    await this.createEntityMutation.mutateAsync({ meetingId: this.data.meetingId, name })
    this.newEntityName = ''
  }

  async importFromAgenda() {
    await this.importFromAgendaMutation.mutateAsync({ meetingId: this.data.meetingId })
  }

  async renameEntity(entity: MeetingEntity, name: string) {
    const trimmedName = name.trim()
    if (trimmedName === '' || trimmedName === entity.name) {
      return
    }
    await this.renameEntityMutation.mutateAsync({ meetingId: this.data.meetingId, meetingEntityId: entity.id, name: trimmedName })
  }

  async moveEntity(entities: MeetingEntity[], index: number, offset: -1 | 1) {
    const targetIndex = index + offset
    if (targetIndex < 0 || targetIndex >= entities.length) {
      return
    }
    const meetingEntityIds = entities.map((entity, position) => {
      if (position === index) {
        return entities[targetIndex].id
      }
      if (position === targetIndex) {
        return entities[index].id
      }
      return entity.id
    })
    await this.reorderEntitiesMutation.mutateAsync({ meetingId: this.data.meetingId, meetingEntityIds })
  }

  async deleteEntity(entity: MeetingEntity) {
    const confirmed = await firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          data: {
            title: this.translateService.instant('MEETING_ENTITIES.DELETE_DIALOG_TITLE', { name: entity.name }),
            description: this.translateService.instant('MEETING_ENTITIES.DELETE_DIALOG_DESCRIPTION'),
          },
        })
        .afterClosed()
    )
    if (confirmed !== true) {
      return
    }
    await this.deleteEntityMutation.mutateAsync({ meetingId: this.data.meetingId, meetingEntityId: entity.id })
  }

  intervalsOf(entity: MeetingEntity, templateItemId: number | null): MeetingEntityInterval[] {
    return entity.intervals.filter((interval) => interval.templateItemId === templateItemId)
  }

  isDrafting(entity: MeetingEntity, templateItemId: number | null): boolean {
    return this.intervalDraft !== null && this.intervalDraft.meetingEntityId === entity.id && this.intervalDraft.templateItemId === templateItemId
  }

  /** The entities an item's minutes are written for: those of the entity-specific item it is, or is under. */
  entitiesOf(item: MeetingEntityTemplateItem, overview: MeetingEntitiesOverview): MeetingEntity[] {
    const scopedItem = this.entitySpecificItemOf(item, overview.templateItems)
    if (scopedItem === null || scopedItem.applicableMeetingEntityIds.length === 0) {
      return overview.entities
    }
    return overview.entities.filter((entity) => scopedItem.applicableMeetingEntityIds.includes(entity.id))
  }

  private entitySpecificItemOf(item: MeetingEntityTemplateItem, items: MeetingEntityTemplateItem[]): MeetingEntityTemplateItem | null {
    if (item.isEntitySpecific) {
      return item
    }
    const parent = item.parentId === null ? null : (items.find((candidate) => candidate.templateItemId === item.parentId) ?? null)
    return parent === null ? null : this.entitySpecificItemOf(parent, items)
  }

  startIntervalDraft(entity: MeetingEntity, templateItemId: number | null) {
    this.intervalDraft = { meetingEntityId: entity.id, templateItemId, start: '', end: '', isInvalid: false }
  }

  cancelIntervalDraft() {
    this.intervalDraft = null
  }

  async saveIntervalDraft(entity: MeetingEntity, templateItemId: number | null) {
    const draft = this.intervalDraft
    if (draft === null || !this.isDrafting(entity, templateItemId)) {
      return
    }
    const startSeconds = parseTimestamp(draft.start)
    const endSeconds = parseTimestamp(draft.end)
    if (startSeconds === null || endSeconds === null || endSeconds <= startSeconds) {
      this.intervalDraft = { ...draft, isInvalid: true }
      return
    }
    const intervals = [...this.intervalsOf(entity, templateItemId).map(toIntervalInput), { startSeconds, endSeconds }].toSorted(
      (first, second) => first.startSeconds - second.startSeconds
    )
    await this.replaceIntervalsMutation.mutateAsync({ meetingId: this.data.meetingId, meetingEntityId: entity.id, templateItemId, intervals })
    this.intervalDraft = null
  }

  async removeInterval(entity: MeetingEntity, removed: MeetingEntityInterval) {
    const intervals = this.intervalsOf(entity, removed.templateItemId)
      .filter((interval) => interval.id !== removed.id)
      .map(toIntervalInput)
    await this.replaceIntervalsMutation.mutateAsync({
      meetingId: this.data.meetingId,
      meetingEntityId: entity.id,
      templateItemId: removed.templateItemId,
      intervals,
    })
  }

  async setEntitySpecific(item: MeetingEntityTemplateItem, isEntitySpecific: boolean) {
    await this.updateTemplateItemScopeMutation.mutateAsync({
      meetingId: this.data.meetingId,
      templateItemId: item.templateItemId,
      isEntitySpecific,
      applicableMeetingEntityIds: isEntitySpecific ? item.applicableMeetingEntityIds : [],
    })
  }

  async setApplicableEntities(item: MeetingEntityTemplateItem, applicableMeetingEntityIds: number[]) {
    await this.updateTemplateItemScopeMutation.mutateAsync({
      meetingId: this.data.meetingId,
      templateItemId: item.templateItemId,
      isEntitySpecific: true,
      applicableMeetingEntityIds,
    })
  }

  async generateEntityMinutes(entity: MeetingEntity) {
    await this.generateEntityMinutesMutation.mutateAsync({ meetingId: this.data.meetingId, meetingEntityId: entity.id })
  }

  async generateAllEntityMinutes() {
    await this.generateAllEntityMinutesMutation.mutateAsync({ meetingId: this.data.meetingId })
  }
}

function toIntervalInput(interval: MeetingEntityInterval) {
  return { startSeconds: interval.startSeconds, endSeconds: interval.endSeconds }
}
